<template>
  <div class="bonuses-page">
    <!-- Bonus Form Modal -->
    <BonusFormModal
      v-if="showModal"
      :mode="modalMode"
      :bonus="selectedBonus"
      @saved="onModalSaved"
      @close="showModal = false"
    />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.bonuses.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.bonuses.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="openCreateModal">{{ $t('admin.bonuses.create_btn') }}</button>
      </div>
    </div>

    <!-- Stat Cards -->
    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-bonuses" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '—' : stats.activeBonuses.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.bonuses.stat_active_bonuses') }}</div>
        <div class="stat-trend up">{{ $t('admin.bonuses.stat_all_running') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-claims" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '—' : stats.claimsToday.toLocaleString() }}</div>
        <div class="stat-label">{{ $t('admin.bonuses.stat_claims_today') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-claim-rate" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '—' : stats.avgClaimRate + '%' }}</div>
        <div class="stat-label">{{ $t('admin.bonuses.stat_avg_claim_rate') }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-icon"><AppIcon name="stat-liability" :size="24" /></div>
        <div class="stat-value">{{ statsLoading ? '—' : formatCents(stats.bonusLiability) }}</div>
        <div class="stat-label">{{ $t('admin.bonuses.stat_bonus_liability') }}</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <select v-model="filters.status" class="filter-select" @change="fetchCampaigns(1)">
        <option value="">{{ $t('admin.bonuses.filter_all_statuses') }}</option>
        <option value="draft">{{ $t('admin.bonuses.filter_draft') }}</option>
        <option value="active">{{ $t('admin.bonuses.filter_active') }}</option>
        <option value="paused">{{ $t('admin.bonuses.filter_paused') }}</option>
        <option value="expired">{{ $t('admin.bonuses.filter_expired') }}</option>
      </select>

      <select v-model="filters.type" class="filter-select" @change="fetchCampaigns(1)">
        <option value="">{{ $t('admin.bonuses.filter_all_types') }}</option>
        <option value="welcome">{{ $t('admin.bonuses.filter_welcome') }}</option>
        <option value="deposit">{{ $t('admin.bonuses.filter_deposit') }}</option>
        <option value="no_deposit">{{ $t('admin.bonuses.filter_no_deposit') }}</option>
        <option value="free_spins">{{ $t('admin.bonuses.filter_free_spins') }}</option>
        <option value="cashback">{{ $t('admin.bonuses.filter_cashback') }}</option>
        <option value="reload">{{ $t('admin.bonuses.filter_reload') }}</option>
        <option value="promo_code">{{ $t('admin.bonuses.filter_promo_code') }}</option>
      </select>

      <div class="search-wrap">
        <input
          v-model="filters.search"
          type="text"
          class="filter-input search-input"
          :placeholder="$t('admin.bonuses.filter_search_placeholder')"
          @keyup.enter="fetchCampaigns(1)"
        >
        <button class="btn-search" @click="fetchCampaigns(1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Campaign Table -->
    <div class="table-container">
      <div v-if="loading" class="table-loading">
        <span class="spinner"></span>
        {{ $t('admin.bonuses.loading') }}
      </div>

      <template v-else>
        <table>
          <thead>
            <tr>
              <th>{{ $t('admin.bonuses.col_name') }}</th>
              <th>{{ $t('admin.bonuses.col_type') }}</th>
              <th>{{ $t('admin.bonuses.col_amount') }}</th>
              <th>{{ $t('admin.bonuses.col_wagering') }}</th>
              <th>{{ $t('admin.bonuses.col_status') }}</th>
              <th>{{ $t('admin.bonuses.col_claims') }}</th>
              <th>{{ $t('admin.bonuses.col_expires') }}</th>
              <th>{{ $t('admin.bonuses.col_actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="campaigns.length > 0">
              <tr v-for="campaign in campaigns" :key="campaign.id">
                <td>
                  <strong class="campaign-name">{{ campaign.name }}</strong>
                </td>
                <td>
                  <span class="badge badge-type" :class="'badge-type-' + campaign.type">
                    {{ typeLabel(campaign.type) }}
                  </span>
                </td>
                <td class="td-amount">{{ formatAmount(campaign) }}</td>
                <td class="td-wagering">{{ formatWagering(campaign) }}</td>
                <td>
                  <span class="badge" :class="statusBadgeClass(campaign.status)">
                    {{ campaign.status }}
                  </span>
                </td>
                <td class="td-claims">
                  {{ campaign.times_claimed.toLocaleString() }}
                  <span v-if="campaign.max_claims !== null" class="claims-max">
                    / {{ campaign.max_claims.toLocaleString() }}
                  </span>
                </td>
                <td class="td-date">{{ formatDate(campaign.expires_at) }}</td>
                <td>
                  <div class="action-buttons">
                    <button class="btn-action" :title="$t('admin.bonuses.action_edit_title')" @click="openEditModal(campaign)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button class="btn-action" :title="$t('admin.bonuses.action_clone_title')" :disabled="cloningId === campaign.id" @click="cloneCampaign(campaign)">
                      <span v-if="cloningId === campaign.id" class="spinner-xs"></span>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                    <button class="btn-action btn-action-danger" :title="$t('admin.bonuses.action_delete_title')" :disabled="deletingId === campaign.id" @click="deleteCampaign(campaign)">
                      <span v-if="deletingId === campaign.id" class="spinner-xs spinner-xs-danger"></span>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="8" class="empty-row">{{ $t('admin.bonuses.no_campaigns') }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="meta.last_page > 1" class="pagination">
          <button
            class="pagination-btn"
            :disabled="meta.current_page <= 1"
            @click="fetchCampaigns(meta.current_page - 1)"
          >
            &larr;
          </button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="pagination-btn"
            :class="{ active: page === meta.current_page }"
            @click="fetchCampaigns(page)"
          >
            {{ page }}
          </button>
          <span class="pagination-info">
            {{ (meta.current_page - 1) * meta.per_page + 1 }}–{{ Math.min(meta.current_page * meta.per_page, meta.total) }} of {{ meta.total }}
          </span>
          <button
            class="pagination-btn"
            :disabled="meta.current_page >= meta.last_page"
            @click="fetchCampaigns(meta.current_page + 1)"
          >
            &rarr;
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'
import BonusFormModal from '@/components/admin/BonusFormModal.vue'

interface ConfigSummary {
  amount?: number
  percentage?: number
  max_bonus?: number
  spins?: number
  wagering_multiplier?: number
  max_cashout?: number
  max_cashback?: number
  period?: string
  game_slug?: string
  code?: string
  max_uses?: number
  spin_value?: number
  min_deposit?: number
}

interface AdminBonus {
  id: number
  name: string
  type: string
  config_summary: ConfigSummary
  status: string
  times_claimed: number
  max_claims: number | null
  starts_at: string | null
  expires_at: string | null
  created_at: string | null
}

interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

interface Stats {
  activeBonuses: number
  claimsToday: number
  avgClaimRate: number
  bonusLiability: number
}

const { t } = useI18n()
const toast = useToastStore()

// Campaigns state
const campaigns = ref<AdminBonus[]>([])
const loading = ref(false)
const meta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

// Stats state
const stats = ref<Stats>({ activeBonuses: 0, claimsToday: 0, avgClaimRate: 0, bonusLiability: 0 })
const statsLoading = ref(false)

// Filters
const filters = ref({
  status: '',
  type: '',
  search: '',
})

// Modal state
const showModal = ref(false)
const modalMode = ref<'create' | 'edit'>('create')
const selectedBonus = ref<AdminBonus | null>(null)

// Per-row async action tracking
const cloningId = ref<number | null>(null)
const deletingId = ref<number | null>(null)

function openCreateModal() {
  selectedBonus.value = null
  modalMode.value = 'create'
  showModal.value = true
}

function openEditModal(campaign: AdminBonus) {
  selectedBonus.value = campaign
  modalMode.value = 'edit'
  showModal.value = true
}

async function onModalSaved() {
  showModal.value = false
  await Promise.all([fetchCampaigns(meta.value.current_page), fetchStats()])
}

async function cloneCampaign(campaign: AdminBonus) {
  if (cloningId.value !== null) return
  cloningId.value = campaign.id
  try {
    await apiClient.post(`/admin/api/bonuses/${campaign.id}/clone`)
    toast.success(t('admin.bonuses.toast_clone_success', { name: campaign.name }))
    await Promise.all([fetchCampaigns(meta.value.current_page), fetchStats()])
  } catch {
    toast.error(t('admin.bonuses.toast_clone_failed'))
  } finally {
    cloningId.value = null
  }
}

async function deleteCampaign(campaign: AdminBonus) {
  if (!confirm(t('admin.bonuses.confirm_delete', { name: campaign.name }))) return
  if (deletingId.value !== null) return
  deletingId.value = campaign.id
  try {
    await apiClient.delete(`/admin/api/bonuses/${campaign.id}`)
    toast.success(t('admin.bonuses.toast_delete_success', { name: campaign.name }))
    await Promise.all([fetchCampaigns(meta.value.current_page), fetchStats()])
  } catch {
    toast.error(t('admin.bonuses.toast_delete_failed'))
  } finally {
    deletingId.value = null
  }
}

// Computed: visible page numbers around current page
const visiblePages = computed(() => {
  const pages: number[] = []
  const total = meta.value.last_page
  const current = meta.value.current_page
  const start = Math.max(1, current - 2)
  const end = Math.min(total, current + 2)
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

// ---------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------

function formatCents(cents: number): string {
  return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function formatAmount(campaign: AdminBonus): string {
  const s = campaign.config_summary
  switch (campaign.type) {
    case 'welcome':
    case 'no_deposit':
      return s.amount != null ? formatCents(s.amount) : '—'
    case 'deposit':
    case 'reload':
      return s.max_bonus != null ? t('admin.bonuses.amount_up_to') + formatCents(s.max_bonus) : (s.percentage != null ? s.percentage + '%' : '—')
    case 'free_spins':
      return s.spins != null ? s.spins.toLocaleString() + t('admin.bonuses.amount_spins') : '—'
    case 'cashback':
      return s.max_cashback != null ? t('admin.bonuses.amount_up_to') + formatCents(s.max_cashback) : (s.percentage != null ? s.percentage + t('admin.bonuses.amount_cashback') : '—')
    case 'promo_code':
      if (s.amount != null) return formatCents(s.amount)
      if (s.percentage != null) return s.percentage + '%'
      return '—'
    default:
      return '—'
  }
}

function formatWagering(campaign: AdminBonus): string {
  const w = campaign.config_summary.wagering_multiplier
  return w != null ? w + 'x' : '—'
}

function typeLabel(type: string): string {
  const keyMap: Record<string, string> = {
    welcome: 'admin.bonuses.type_welcome',
    deposit: 'admin.bonuses.type_deposit',
    no_deposit: 'admin.bonuses.type_no_deposit',
    free_spins: 'admin.bonuses.type_free_spins',
    cashback: 'admin.bonuses.type_cashback',
    reload: 'admin.bonuses.type_reload',
    promo_code: 'admin.bonuses.type_promo_code',
  }
  return keyMap[type] != null ? t(keyMap[type]) : type
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'active':
      return 'badge-status-active'
    case 'paused':
      return 'badge-status-paused'
    case 'draft':
      return 'badge-status-draft'
    case 'expired':
      return 'badge-status-expired'
    default:
      return 'badge-status-draft'
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

// ---------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------

async function fetchCampaigns(page: number): Promise<void> {
  loading.value = true

  try {
    const params: Record<string, string | number> = { page, per_page: 15 }
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.type) params.type = filters.value.type
    if (filters.value.search) params.search = filters.value.search

    const response = await apiClient.get('/admin/api/bonuses', { params })
    campaigns.value = response.data.data as AdminBonus[]
    meta.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
    }
  } catch {
    toast.error(t('admin.bonuses.toast_load_failed'))
  } finally {
    loading.value = false
  }
}

async function fetchStats(): Promise<void> {
  statsLoading.value = true

  try {
    const [activeCampaignsRes, todayRes, allRes] = await Promise.all([
      apiClient.get('/admin/api/bonuses', { params: { status: 'active', per_page: 1 } }),
      apiClient.get('/admin/api/bonuses/reports', { params: { period: 'today' } }),
      apiClient.get('/admin/api/bonuses/reports', { params: { period: 'all' } }),
    ])

    stats.value = {
      activeBonuses: activeCampaignsRes.data.total as number,
      claimsToday: todayRes.data.total_count as number,
      avgClaimRate: allRes.data.average_conversion_rate as number,
      bonusLiability: allRes.data.net_cost as number,
    }
  } catch {
    toast.error(t('admin.bonuses.toast_stats_failed'))
  } finally {
    statsLoading.value = false
  }
}

onMounted(() => {
  fetchCampaigns(1)
  fetchStats()
})
</script>

<style scoped>
.bonuses-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
  letter-spacing: 0.02em;
}

.btn-primary:hover {
  background: #00cc01;
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

/* Stat Grid */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  transition: all 150ms ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.1);
}

.stat-icon {
  font-size: 24px;
  margin-bottom: 12px;
}

.stat-value {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.stat-label {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
}

.stat-trend {
  font-size: 12px;
  font-weight: 500;
}

.stat-trend.up {
  color: var(--color-success, #00e701);
}

/* Filters */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select,
.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
}

.filter-select option {
  background: var(--bg-secondary, #1a2c38);
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.search-wrap {
  display: flex;
}

.search-input {
  border-radius: var(--radius-md, 8px) 0 0 var(--radius-md, 8px);
  width: 220px;
}

.btn-search {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: none;
  border-radius: 0 var(--radius-md, 8px) var(--radius-md, 8px) 0;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  transition: color 150ms ease;
}

.btn-search:hover {
  color: var(--accent-primary, #00e701);
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

tbody tr {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
}

tbody tr:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

.campaign-name {
  font-weight: 600;
  color: var(--text-primary, #fff);
  font-size: 13px;
}

.td-amount {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-weight: 600;
  color: var(--text-primary, #fff);
}

.td-wagering {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-secondary, #b1bad3);
}

.td-claims {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  color: var(--text-primary, #fff);
}

.claims-max {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 12px;
}

.td-date {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

/* Status badges */
.badge-status-active {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-status-paused {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-status-draft {
  background: rgba(127, 143, 164, 0.15);
  color: var(--text-tertiary, #7f8fa4);
}

.badge-status-expired {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

/* Type badges */
.badge-type {
  background: rgba(20, 117, 225, 0.15);
  color: #5ba7f0;
}

/* Action buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-action:hover {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

.btn-action-danger:hover {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
  border-color: rgba(237, 66, 69, 0.3);
}

/* Empty row */
.empty-row {
  text-align: center;
  padding: 48px 20px !important;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled):not(.active) {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-xs {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(0, 231, 1, 0.25);
  border-top-color: var(--accent-primary, #00e701);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-xs-danger {
  border-color: rgba(237, 66, 69, 0.25);
  border-top-color: var(--color-danger, #ed4245);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 1200px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .bonuses-page {
    padding: 16px;
  }

  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .filters-row {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }
}
</style>
